import type { StationId } from '@/domain/types';
import type {
  LocalAuthListEntryRow,
  LocalAuthListRepository,
} from '@/persistence/ports';
import type { Evse3Database } from '../db';

/**
 * SPEC-001 — Dexie-backed Local Authorization List repository.
 * Primary key is the compound `[stationId+idTag]`, matching the
 * schema declared in data/storage-schema.yaml#localAuthList.
 */
export class LocalAuthListRepositoryDexie implements LocalAuthListRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly LocalAuthListEntryRow[]> {
    return this.db.localAuthList.where('stationId').equals(stationId).toArray();
  }

  async get(stationId: StationId, idTag: string): Promise<LocalAuthListEntryRow | null> {
    const row = await this.db.localAuthList.get([stationId, idTag]);
    return row ?? null;
  }

  async upsert(row: LocalAuthListEntryRow): Promise<void> {
    await this.db.localAuthList.put(row);
  }

  async delete(stationId: StationId, idTag: string): Promise<void> {
    await this.db.localAuthList.delete([stationId, idTag]);
  }

  async replaceAll(
    stationId: StationId,
    rows: readonly LocalAuthListEntryRow[],
  ): Promise<void> {
    // Atomic: wipe + bulk-insert in one rw transaction so a partial
    // failure doesn't leave a mix of old + new rows for this station.
    await this.db.transaction('rw', this.db.localAuthList, async () => {
      await this.db.localAuthList.where('stationId').equals(stationId).delete();
      if (rows.length > 0) {
        await this.db.localAuthList.bulkPut(rows.map((r) => ({ ...r })));
      }
    });
  }

  async clear(stationId: StationId): Promise<void> {
    await this.db.localAuthList.where('stationId').equals(stationId).delete();
  }
}
